export type PublicExperienceKey = "generic" | "clinics" | "legal" | "psychology";

export type DemoMessage = { at: number; sender: "customer" | "assistant"; text: string };
export type NichePresentation = {
  key: PublicExperienceKey;
  segmentLabel: string;
  eyebrow: string;
  title: { before: string; emphasis: string };
  description: string;
  demo: { company: string; subtitle: string; initials: string; tone: "clinic" | "legal" | "psychology"; messages: readonly DemoMessage[] };
};

const clinicDemo = {
  company: "Clínica Bem Sorrir",
  subtitle: "Odontologia · Assistente G3.AI",
  initials: "BS",
  tone: "clinic" as const,
  messages: [
    { at: 1200, sender: "customer" as const, text: "Oi, boa tarde! Vocês fazem clareamento dental?" },
    { at: 7500, sender: "assistant" as const, text: "Boa tarde! 😊 Seja bem-vindo à Clínica Bem Sorrir! Trabalhamos com avaliação para clareamento dental. Posso te explicar como funciona?" },
    { at: 18000, sender: "customer" as const, text: "Queria saber se quem tem dentes sensíveis também pode fazer." },
    { at: 26000, sender: "assistant" as const, text: "Essa é uma dúvida muito comum! Nesse caso, o dentista precisa avaliar a sensibilidade antes de indicar o tratamento mais adequado." },
    { at: 37000, sender: "customer" as const, text: "Entendi! E vocês também trabalham com aparelho transparente?" },
    { at: 48000, sender: "assistant" as const, text: "Temos opções de tratamentos ortodônticos. O profissional avalia cada caso para indicar a melhor alternativa. Posso te explicar como funciona essa avaliação." },
    { at: 60000, sender: "customer" as const, text: "Ah, legal! Muito obrigado pelas informações 😊" },
    { at: 66000, sender: "assistant" as const, text: "Por nada! 💙 Sempre que precisar, estamos por aqui para ajudar!" }
  ]
};

const presentations: Record<PublicExperienceKey, NichePresentation> = {
  generic: {
    key: "generic", segmentLabel: "sua empresa", eyebrow: "ATENDIMENTO QUE APROXIMA",
    title: { before: "Você cuida do seu negócio.", emphasis: "A G3.AI atende seus clientes no WhatsApp." },
    description: "Seu WhatsApp pode acolher dúvidas, apresentar as informações que sua empresa definiu e organizar cada conversa com clareza.",
    demo: clinicDemo
  },
  clinics: {
    key: "clinics", segmentLabel: "clínicas", eyebrow: "ATENDIMENTO PARA CLÍNICAS",
    title: { before: "Sua equipe cuida das pessoas.", emphasis: "A G3.AI organiza o primeiro contato." },
    description: "Receba dúvidas iniciais com uma comunicação clara e encaminhe cada conversa para a equipe, sempre com as informações que a clínica definiu.",
    demo: clinicDemo
  },
  legal: {
    key: "legal", segmentLabel: "advocacia", eyebrow: "ATENDIMENTO PARA ESCRITÓRIOS",
    title: { before: "Seu escritório cuida do caso.", emphasis: "A G3.AI organiza o primeiro contato." },
    description: "Registre o contexto inicial, responda com as informações aprovadas pelo escritório e direcione cada solicitação para a equipe responsável.",
    demo: {
      company: "Almeida & Costa Advocacia", subtitle: "Triagem inicial · Assistente G3.AI", initials: "AC", tone: "legal",
      messages: [
        { at: 700, sender: "customer", text: "Olá! Gostaria de entender como funciona uma primeira conversa sobre uma questão contratual." },
        { at: 3300, sender: "assistant", text: "Olá! Posso registrar o contexto inicial da sua solicitação e encaminhar para a equipe avaliar o próximo passo." },
        { at: 9300, sender: "customer", text: "Posso enviar alguns detalhes para vocês?" },
        { at: 12500, sender: "assistant", text: "Claro. Você pode compartilhar as informações que considerar relevantes, e a equipe organiza o retorno adequado." },
        { at: 18700, sender: "customer", text: "Obrigado, vou explicar por escrito." },
        { at: 21900, sender: "assistant", text: "Perfeito. Assim conseguimos deixar seu primeiro contato bem organizado." }
      ]
    }
  },
  psychology: {
    key: "psychology", segmentLabel: "psicologia", eyebrow: "ATENDIMENTO PARA PSICOLOGIA",
    title: { before: "Você acolhe com atenção.", emphasis: "A G3.AI organiza o primeiro contato." },
    description: "Apresente o processo definido pelo espaço, registre a preferência de contato e mantenha a conversa organizada para a equipe.",
    demo: {
      company: "Espaço Horizonte Psicologia", subtitle: "Primeiro contato · Assistente G3.AI", initials: "EH", tone: "psychology",
      messages: [
        { at: 1000, sender: "customer", text: "Olá! Gostaria de saber como funciona o primeiro contato." },
        { at: 5700, sender: "assistant", text: "Olá! Esta é uma demonstração fictícia. Posso explicar como o espaço organiza os primeiros contatos e registrar sua preferência para retorno." },
        { at: 14500, sender: "customer", text: "Vocês atendem presencialmente ou on-line?" },
        { at: 19400, sender: "assistant", text: "A equipe poderá apresentar as modalidades que definiu e combinar o próximo passo diretamente com você." },
        { at: 27800, sender: "customer", text: "Obrigada, vou aguardar o retorno." },
        { at: 32500, sender: "assistant", text: "Combinado. Sua conversa fica registrada para a continuidade do atendimento." }
      ]
    }
  }
};

export function publicExperienceKey(value?: string | null): PublicExperienceKey | null {
  return value && value in presentations ? value as PublicExperienceKey : null;
}

export function presentationFor(value?: string | null): NichePresentation {
  return presentations[publicExperienceKey(value) ?? "generic"];
}
